import React from "react"
import styled from "styled-components"
import { Container } from "../styles/Container"
import { breakpoint, size } from "../layout/styles"
import ReactHtmlParser from 'react-html-parser'
import CommonSlider from "./CommonSlider"

/**
 * A slider component designed to display testimonials.
 * It fetches testimonial data passed as props and renders each testimonial
 * using a common slider component.
 *
 * @param {Object} data - The testimonial data object.
 * @param {string} data.heading - The main heading for the testimonials section.
 * @param {string} data.content - Any additional content before the testimonials.
 * @param {Array} data.items - An array of testimonial items. Each item contains:
 *  - testimonial_title: The title or headline for the testimonial.
 *  - testimonial_content: The main content of the testimonial.
 *  - testimonial_reviewer: The name of the person who gave the testimonial.
 */
const TestimonialsSlider = ({ data }) => {
  // Convert string sizes to numeric for use with breakpoints
  const numericSizes = Object.fromEntries(
    Object.entries(size).map(([key, value]) => [key, parseInt(value, 10)])
  );

  // Define breakpoints for the slider
  const swiperBreakpoints = {
    [numericSizes.mobile]: {
      slidesPerView: 1
    },
    [numericSizes.tablet]: {
      slidesPerView: 1
    },
    [numericSizes.desktop]: {
      slidesPerView: 1,
    },
  };

  return (
    <Wrapper>
      {data.heading && <h2>
        {data.heading}
      </h2>}
      {data.content && <Body>
        {ReactHtmlParser(data.content)}
      </Body>}
      {data.items?.length > 0 && <Items
        slidesPerView={1}
        usePagination={true}
        breakpoints={swiperBreakpoints}
        items={data.items}
        itemBuilder={(item) => <Item>
          {item.testimonial_title &&
            <TestimonialTitle>
              {item.testimonial_title}
            </TestimonialTitle>}
          {item.testimonial_content &&
            <TestimonialContent>
              {ReactHtmlParser(item.testimonial_content)}
            </TestimonialContent>}
          {item.testimonial_reviewer &&
            <TestimonialReviewer>
              {item.testimonial_reviewer}
            </TestimonialReviewer>}
        </Item>}
      />}

    </Wrapper>
  )
}

export default TestimonialsSlider

const Wrapper = styled(Container)`
  margin: 36px 20px;
  @media ${breakpoint.desktop} {
    margin: 72px auto;
  }
`

const Body = styled.div<any>`
  margin-bottom: 36px;
`

const Items = styled(CommonSlider)`
  background-color: var(--black);
  color: var(--background);

  &::before,
  &::after {
    position: absolute;
    height: 45px;
    width: 65px;
  }

  &::before {
    content: url(/images/quotes_left.svg);
    top: 25px;
    left: 56px;
    filter: brightness(0) invert(1);
  }

  &::after {
    content: url(/images/quotes_right.svg);
    right: 56px;
    bottom: 25px;
    filter: brightness(0) invert(1);
  }

  &&& {
    .swiper-pagination {
      left: 44px;
      right: unset;
      width: auto;
      bottom: 36px;

      @media ${breakpoint.desktop} {
        left: 172px;
      }
    }

    .swiper-pagination-bullet {
      width: 20px;
      height: 20px;
      margin: 0 6px;
      border-color: var(--white);

      @media ${breakpoint.desktop} {
        width: 12px;
        height: 12px;
      }
    }
    
    .swiper-pagination-bullet-active {
      border-color: var(--lightblue);
      background: var(--lightblue);
    }
  }
`

const Item = styled.div<any>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  margin: 100px 10px 100px 20px;

  @media ${breakpoint.desktop} {
    margin: 65px 150px 90px;
  }
`

const TestimonialTitle = styled.span<any> `
  font-size: 2.5em;
  font-weight: 400;
  margin-bottom: 10px;
`

const TestimonialContent = styled.div<any>`
  line-height: 25px;
  margin-bottom: 16px;
`

const TestimonialReviewer = styled.span<any>`
  font-size: .75em;
  text-transform: uppercase;
  line-height: 18px;
`
